import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Shield, TrendingUp, FileText, MessageCircle, CheckCircle, Star, ArrowRight, Zap, Lock, Clock, ChevronDown, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const features = [
    {
      icon: <Zap className="h-6 w-6 text-teal-600" />,
      title: 'Automatic Disputes',
      description: 'AI generates and sends dispute letters automatically after authorization.'
    },
    {
      icon: <Shield className="h-6 w-6 text-teal-600" />,
      title: 'Secure & Compliant',
      description: 'Bank-level encryption with full FCRA compliance for your protection.'
    },
    {
      icon: <MessageCircle className="h-6 w-6 text-teal-600" />,
      title: 'AI Credit Coach',
      description: 'Get personalized guidance and answers to your credit questions 24/7.'
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-teal-600" />,
      title: 'Track Progress',
      description: 'Monitor your disputes and see your credit improvement journey.'
    },
    {
      icon: <FileText className="h-6 w-6 text-teal-600" />,
      title: 'Document Vault',
      description: 'Securely store IDs and supporting documents in one place.'
    },
    {
      icon: <Clock className="h-6 w-6 text-teal-600" />,
      title: 'Monthly Automation',
      description: 'Set it and forget it with automated monthly dispute cycles.'
    }
  ];

  const steps = [
    { number: '01', title: 'Sign Up', description: 'Create your account in under 2 minutes' },
    { number: '02', title: 'Add Credit Items', description: 'Enter items from your credit report' },
    { number: '03', title: 'Authorize', description: 'Provide authorization for auto-disputes' },
    { number: '04', title: 'Relax', description: 'We handle the disputes automatically' }
  ];

  const faqs = [
    {
      question: 'How does AICreditPilot work?',
      answer: 'AICreditPilot uses AI to analyze your credit report items, identify inaccuracies, and automatically generate compliant dispute letters. Once you authorize us, we send these letters to credit bureaus on your behalf and track the results.'
    },
    {
      question: 'Is this service guaranteed to improve my credit score?',
      answer: 'No credit repair service can guarantee specific results. AICreditPilot is an educational and assistance-based service that helps you dispute inaccurate information on your credit report. Results vary based on individual circumstances and the accuracy of reported items.'
    },
    {
      question: 'How many disputes can I send per month?',
      answer: 'Premium members can send up to 5 dispute letters per month. This limit is designed to follow best practices and avoid appearing frivolous to credit bureaus, which can actually hurt your case.'
    },
    {
      question: 'What credit bureaus do you send disputes to?',
      answer: 'We send dispute letters to all three major credit bureaus: Experian, Equifax, and TransUnion. Each dispute is customized based on where the inaccurate item is being reported.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your Premium subscription at any time. There are no long-term contracts or cancellation fees. Your access continues until the end of your current billing period.'
    },
    {
      question: 'Is my personal information secure?',
      answer: 'Absolutely. We use bank-level encryption to protect your data both in transit and at rest. Your information is never sold or shared with third parties, and we are fully compliant with data protection regulations.'
    },
    {
      question: 'What is the AI Credit Coach?',
      answer: 'The AI Credit Coach is your 24/7 credit advisor. You can ask questions about credit repair, get personalized recommendations, and receive a customized 30/90 day action plan to improve your credit. It\'s included free with Premium membership.'
    },
    {
      question: 'How long does it take to see results?',
      answer: 'Credit bureaus are required by law to investigate disputes within 30-45 days. You may start seeing changes within this timeframe. However, meaningful credit improvement often takes 3-6 months of consistent effort.'
    }
  ];

  function toggleFaq(index) {
    if (openFaq === index) {
      setOpenFaq(null);
    } else {
      setOpenFaq(index);
    }
  }

  async function handleContactSubmit(e) {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    // Simulate sending - in production, this would call an API
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Message sent! We\'ll get back to you soon.');
    setContactForm({ name: '', email: '', message: '' });
    setSending(false);
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                AICreditPilot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition-colors">Contact</a>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 transition-colors">Blog</Link>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    Log In
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="btn-teal"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-teal-50/30" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                AI-Powered Credit Repair
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
                Fix Your Credit on
                <span className="text-gradient block">Autopilot</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                AICreditPilot automatically identifies, generates, and sends dispute letters to credit bureaus. 
                No manual work required after authorization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/register')}
                  className="btn-primary text-base h-12 px-8"
                  data-testid="hero-cta-btn"
                >
                  Start Free Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="btn-secondary text-base h-12"
                  data-testid="view-pricing-btn"
                >
                  View Pricing
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-slate-100">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">Trusted by 10,000+ users</p>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in">
              <div className="absolute -inset-4 bg-gradient-to-r from-teal-100 to-slate-100 rounded-3xl blur-3xl opacity-50" />
              <img 
                src="https://images.unsplash.com/photo-1758525588839-97e6313401e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Credit improvement on mobile"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Everything You Need to Improve Your Credit
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful features designed to make credit repair effortless and effective.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-8 hover:border-teal-500/30 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              How AICreditPilot Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Four simple steps to automate your credit repair journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <div className="text-7xl font-bold text-slate-100 mb-4" style={{ fontFamily: 'Manrope' }}>
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  {step.title}
                </h3>
                <p className="text-slate-600">
                  {step.description}
                </p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 right-0 w-1/2 border-t-2 border-dashed border-slate-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Start free, upgrade when you're ready for automation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="flex flex-col p-8 bg-slate-800 border border-slate-700 rounded-2xl" data-testid="free-plan-card">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>Free Plan</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">$0</span>
                  <span className="text-slate-400">/month</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                {['Credit insights', 'Educational resources', 'Manual letter download', 'Basic support'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-slate-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                variant="outline"
                onClick={() => navigate('/register')}
                className="w-full h-12 rounded-full border-slate-600 text-white hover:bg-slate-700"
                data-testid="free-plan-btn"
              >
                Get Started Free
              </Button>
            </div>
            {/* Premium Plan */}
            <div className="flex flex-col p-8 bg-white border-2 border-teal-500 rounded-2xl relative" data-testid="premium-plan-card">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                Most Popular
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Premium Plan</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">$29.99</span>
                  <span className="text-slate-600">/month</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-grow">
                {[
                  'Everything in Free',
                  '5 dispute letters per month',
                  'Automatic dispute sending',
                  'Monthly automation cycles',
                  'AI Credit Coach (unlimited)',
                  'Real-time monitoring & alerts',
                  'Priority support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-teal-600" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/register?plan=premium')}
                className="w-full btn-teal h-12"
                data-testid="premium-plan-btn"
              >
                Start Premium Trial
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-32 bg-slate-50/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Got questions? We've got answers.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900 pr-4" style={{ fontFamily: 'Manrope' }}>
                    {faq.question}
                  </span>
                  <ChevronDown className={`h-5 w-5 text-slate-500 shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
                Get in Touch
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Have questions or need help? We're here for you. Reach out and our team will get back to you within 24 hours.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Mail className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Email Us</h3>
                    <p className="text-slate-600">support@aicreditpilot.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <Phone className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Call Us</h3>
                    <p className="text-slate-600">1-800-697-4877</p>
                    <p className="text-sm text-slate-500">Mon-Fri 9am-6pm EST</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Location</h3>
                    <p className="text-slate-600">United States</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>
                Send Us a Message
              </h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Name</Label>
                  <Input
                    id="contact-name"
                    placeholder="Your name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    className="h-11"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="you@example.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    className="h-11"
                    data-testid="contact-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    placeholder="How can we help you?"
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    rows={4}
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-teal h-11"
                  disabled={sending}
                  data-testid="contact-submit"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to Take Control of Your Credit?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Join thousands of users who are improving their credit scores with AICreditPilot.
          </p>
          <Button 
            onClick={() => navigate('/register')}
            className="btn-primary text-base h-12 px-10"
            data-testid="final-cta-btn"
          >
            Get Started for Free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-500" />
              <span className="font-bold text-white" style={{ fontFamily: 'Manrope' }}>AICreditPilot</span>
            </div>
            <p className="text-slate-400 text-sm text-center">
              Disclaimer: AICreditPilot is an educational and assistance-based service. 
              No specific credit score increases are guaranteed. Users may cancel at any time.
            </p>
            <div className="flex items-center gap-4 text-slate-400 text-sm">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
